import { createFileRoute, Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useAdminUserDetail } from '@/features/admin/services/admin.convex'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export const Route = createFileRoute(
  '/(dashboard)/admin/users/$userId/organizations/',
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    name: (search.name as string) || undefined,
  }),
  beforeLoad: ({ params, search }) => ({
    breadcrumbs: [
      { title: 'Admin' },
      { title: 'Users', href: '/admin/users' },
      { title: search.name || params.userId },
      { title: 'Organizations' },
    ],
  }),
})

function RouteComponent() {
  const { userId } = Route.useParams()
  const { data: user } = useAdminUserDetail(userId)

  if (!user) return null

  return (
    <div className="bg-muted/30 rounded-lg p-6">
      <div className="relative w-full overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-muted/50 border-b transition-colors">
              <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                Organization
              </TableHead>
              <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                Type
              </TableHead>
              <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                Role
              </TableHead>
              <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                Member Since
              </TableHead>
              <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {user.organizations.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="p-2 align-middle text-center text-muted-foreground py-12"
                >
                  Not a member of any organization
                </TableCell>
              </TableRow>
            )}
            {user.organizations.map((org) => {
              if (!org) return null
              return (
                <TableRow
                  key={org.id}
                  className="hover:bg-muted/50 border-b transition-colors"
                >
                  <TableCell className="p-2 align-middle whitespace-nowrap font-medium">
                    {org.name}
                  </TableCell>
                  <TableCell className="p-2 align-middle whitespace-nowrap">
                    <Badge variant={org.isAdmin ? 'default' : 'secondary'}>
                      {org.isAdmin ? 'Platform' : 'Customer'}
                    </Badge>
                  </TableCell>
                  <TableCell className="p-2 align-middle whitespace-nowrap">
                    {org.role ? (
                      <Badge variant="outline">{org.role.name}</Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">
                        No role
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="p-2 align-middle whitespace-nowrap text-sm text-muted-foreground">
                    {new Date(org.memberSince).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="p-2 align-middle whitespace-nowrap">
                    <Button variant="outline" size="sm" asChild>
                      <Link
                        to="/admin/organizations/$organizationId/members"
                        params={{ organizationId: org.id }}
                        search={{ name: org.name }}
                      >
                        View Organization
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
